import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Stack,
  Button,
  Container,
  Typography,
  useTheme,
  Alert,
  Skeleton,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StoryAlbumCard from '../components/StoryAlbumCard';
import { useSearchParams } from 'react-router-dom';
import * as storiesApi from '../../../../api/stories';
import axios from 'axios';
import ReaderSiteHeader from '../../../../shared/ui/ReaderSiteHeader';

const STORIES_PER_PAGE = 4;

type ApiErrorShape = {
  error?: string;
  message?: string;
  exceptionType?: string;
  statusCode?: number;
  [k: string]: any;
};

const StoryAlbumContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();

  const initialSearch = searchParams.get('search') || '';
  const initialPage = parseInt(searchParams.get('page') || '0', 10);

  const [searchInput, setSearchInput] = useState(initialSearch);
  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(isNaN(initialPage) ? 0 : initialPage);
  const [stories, setStories] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const params: any = {};
    if (search) params.search = search;
    if (page > 0) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [search, page, setSearchParams]);

  const loadStories = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const response = await storiesApi.getAlbums(page, STORIES_PER_PAGE, search, 0);
      setStories(response.data.items);
      setTotalPages(response.data.totalPages);
    } catch (err: any) {
      // ====== leitura de falha no padrão ReaderPage ======
      let msg = 'A server error occurred. Please try again later.';
      if (axios.isAxiosError(err)) {
        const status: number | undefined =
          err.response?.status ?? err?.status ?? err.response?.data?.statusCode;

        const data: ApiErrorShape | string | undefined = (err as any).response?.data ?? (err as any).data;

        if (status && status !== 500) {
          msg =
            (typeof data === 'string' && data) ||
            (typeof data === 'object' && (data.error || data.message)) ||
            'Could not load stories.';
        }
      }
      setErrorMsg(msg);
      setStories([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    void loadStories();
  }, [loadStories]);

  const loadingCards = Array.from({ length: 3 }, (_, index) => index);

  // ===== UI de erro centralizada com Retry (igual ao exemplo) =====
  if (errorMsg) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 560, width: '100%' }}>
          {errorMsg}
        </Alert>
        <Button
          variant="contained"
          onClick={() => loadStories()}
          disabled={loading}
          sx={{ minWidth: 200 }}
        >
          {loading ? 'Loading…' : 'Try again'}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, py: 4, minHeight: '100vh' }}>
      <ReaderSiteHeader
        mode="stories"
        title="Story library"
        subtitle="Browse the standard story catalog, sign in when needed, and return to your chapter without friction."
      />

      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
          Explore stories
        </Typography>
        <Typography sx={{ mb: 4, color: theme.palette.text.secondary }}>
          Search the catalog and open any chapter from the list below.
        </Typography>

        <TextField
          fullWidth
          placeholder="Search by story name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearch(searchInput);
              setPage(0);
            }
          }}
          variant="filled"
          sx={{
            mb: 4,
            input: { color: theme.palette.text.primary },
            '& .MuiFilledInput-root': {
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              color: theme.palette.text.primary,
            },
          }}
        />

        <Stack spacing={4}>
          {loading ? (
            loadingCards.map((item) => (
              <Paper key={item} sx={{ p: 2, backgroundColor: theme.palette.background.paper }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Skeleton variant="rectangular" width={200} height={260} />
                  <Stack spacing={1.5} sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="40%" height={48} />
                    <Skeleton variant="rectangular" height={80} />
                    <Skeleton variant="text" width="20%" />
                    <Skeleton variant="rectangular" height={120} />
                  </Stack>
                </Stack>
              </Paper>
            ))
          ) : stories.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: theme.palette.background.paper }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                No stories found
              </Typography>
              <Typography color={theme.palette.text.secondary}>
                Try a different search term or clear the current filter to see more stories.
              </Typography>
            </Paper>
          ) : (
            stories.map((story) => <StoryAlbumCard key={story.id} data={story} freeMode={false}/>)
          )}
        </Stack>

        {totalPages > 1 && (
          <Stack direction="row" justifyContent="center" gap={2} mt={4}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: theme.palette.background.paper,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                },
              }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              sx={{
                color: theme.palette.text.primary,
                borderColor: theme.palette.text.primary,
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: theme.palette.background.paper,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                },
              }}
            >
              Next
            </Button>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default StoryAlbumContent;
